import { css, styled } from 'canoe';
import { useFormikContext } from 'formik';
import get from 'lodash/get';
import React from 'react';

import { DefaultItemProps, Select } from '@candu-ai/analytics/ui';
import { SelectInputWrapper } from 'dashboard/pages/Segment/components/DefinitionFormFields/DefinitionFormComponents';
import { segmentTypeOptions } from 'dashboard/pages/Segment/components/constants';
import { DefinitionFormikValues } from 'dashboard/pages/Segment/components/types';

import { DefaultRule } from './DefaultRule';
import { EventOccurredRule } from './EventOccurredRule';
import { InSegmentRule } from './InSegmentRule';
import { LimitedTimeContentRule } from './LimitedTimeContentRule';
import { UserPropertyRule } from './UserPropertyRule';
import { UserSeenRule } from './UserSeenRule';

const FieldsWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.spaces.s};
    flex-grow: 1;
  `}
`;

interface RenderRuleProps {
  values: DefinitionFormikValues;
  base: string;
  readOnly?: boolean;
}

const renderRule = ({ values, base, readOnly }: RenderRuleProps) => {
  const ruleValue = get(values, base);
  const ruleType = ruleValue.type;

  if (ruleType === 'UserProperty') {
    return <UserPropertyRule base={base} readOnly={readOnly} />;
  }

  if (ruleType === 'EventOccurred') {
    return <EventOccurredRule base={base} readOnly={readOnly} />;
  }

  if (ruleType === 'FirstSeen' || ruleType === 'LastSeen') {
    return <UserSeenRule base={base} readOnly={readOnly} />;
  }

  if (ruleType === 'LimitedTimeContent') {
    return <LimitedTimeContentRule base={base} readOnly={readOnly} />;
  }

  if (ruleType === 'ISR') {
    return <InSegmentRule base={base} readOnly={readOnly} />;
  }

  return <DefaultRule base={base} ruleType={ruleType} readOnly={readOnly} />;
};

export interface RuleProps {
  base: string;
  readOnly?: boolean;
}

export const Rule = ({ base, readOnly }: RuleProps) => {
  const { values, setFieldValue } = useFormikContext<DefinitionFormikValues>();

  const ruleTypePath = `${base}.type`;

  const value = get(values, ruleTypePath);

  return (
    <FieldsWrapper data-cy="segment-definition-rule-component">
      <Select<DefaultItemProps, DefinitionFormikValues>
        placeholder="Choose a property"
        name={ruleTypePath}
        options={segmentTypeOptions}
        readOnly={readOnly}
        InputWrapper={SelectInputWrapper}
        data-cy="segment-definition-selector"
        values={values}
        setFieldValue={setFieldValue}
        validateOnSelectWithErrors
      />
      {value && value !== '' && renderRule({ values, base, readOnly })}
    </FieldsWrapper>
  );
};
